import { useState, useEffect, useCallback } from "react";
import { usePoll } from "@/hooks/usePoll";
import { getHistory, type HistoryFilter, type HistoryItem } from "../api/history-client";

const POLL_INTERVAL_MS = 30_000;

export interface UseHistoryResult {
  items: HistoryItem[];
  isLoading: boolean;
  error: string | null;
  refresh: () => void;
}

export function useHistory({ filter }: { filter: HistoryFilter }): UseHistoryResult {
  const [items, setItems] = useState<HistoryItem[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setError(null);
    try {
      const data = await getHistory(filter);
      setItems(data);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed to load history");
    } finally {
      setIsLoading(false);
    }
  }, [filter]);

  // Initial fetch + re-fetch on filter change
  useEffect(() => {
    setIsLoading(true);
    void load();
  }, [load]);

  // Tab-visibility-aware polling via usePoll (pauses when tab is hidden).
  usePoll(load, POLL_INTERVAL_MS);

  return { items, isLoading, error, refresh: load };
}
